"use client";

import { useState } from "react";
import {
  DataTable,
  Notice,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD, HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { CurrencyField, useMoney } from "@/components/tools/finance/shared";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { parseNumberOr } from "@/lib/parse-number";
import {
  budgetTotals,
  INCOME_ITEMS,
  NEEDS_ITEMS,
  SAVINGS_ITEMS,
  splitRows,
  sumAmounts,
  termGap,
  WANTS_ITEMS,
} from "@/lib/tool-kit/finance/budget";

/** English strings. A translated content file overrides these through `ui`. */
export const STUDENT_BUDGET_CALCULATOR_STRINGS = {
  currency: "Currency",
  plainNumbers: "No currency (plain numbers)",
  monthlyHint:
    "Enter monthly amounts after tax — what actually reaches your account. Leave a line at 0 if it does not apply.",
  incomeHeading: "Monthly income",
  needsHeading: "Needs",
  wantsHeading: "Wants",
  savingsHeading: "Savings and debt repayment",
  subtotal: "Subtotal",
  itemJob: "Job or work-study (after tax)",
  itemSupport: "Family support",
  itemOther: "Other income",
  itemRent: "Rent or housing",
  itemUtilities: "Utilities",
  itemGroceries: "Groceries",
  itemTransport: "Transport",
  itemPhone: "Phone and internet",
  itemHealth: "Health and insurance",
  itemSupplies: "Books and supplies",
  itemEatingOut: "Eating out",
  itemEntertainment: "Entertainment",
  itemSubscriptions: "Subscriptions",
  itemPersonal: "Personal and other",
  itemSavings: "Savings",
  itemDebt: "Debt payments",
  termHeading: "One term",
  termMonths: "Months in a term",
  termsPerYear: "Terms per year",
  tuition: "Tuition and fees per term",
  aid: "Aid per term (grants, scholarships, loans)",
  resultsHeading: "Monthly balance",
  monthlyIncome: "Monthly income",
  monthlySpending: "Monthly spending",
  leftOver: "Left over each month",
  shortBy: "Short each month",
  splitHeading: "Compared with the 50/30/20 guideline",
  splitNote:
    "50/30/20 is a rule of thumb — about 50% of after-tax income to needs, 30% to wants and 20% to savings and debt repayment. It is a guide, not a target everyone should hit; student needs often run higher.",
  groupColumn: "Group",
  amountColumn: "Your amount",
  shareColumn: "Your share of income",
  targetColumn: "Guideline amount",
  differenceColumn: "Over (+) or under (−)",
  noIncomeShare: "—",
  termResultsHeading: "Funding gap for one term",
  termCost: "Cost of the term",
  termResources: "Aid plus income",
  termGapLabel: "Gap per term",
  termSurplusLabel: "Surplus per term",
  yearGapLabel: "Gap per year",
  yearSurplusLabel: "Surplus per year",
  perMonthLabel: "Gap spread over the months",
  perMonthSurplusLabel: "Surplus spread over the months",
  termNote:
    "Cost of the term is tuition and fees plus the months of spending above. Aid plus income is the aid per term plus the months of income. The gap is the difference — what savings, new aid or a change in spending would have to cover.",
  needIncome: "Enter some income to compare with the 50/30/20 guideline.",
} as const;

type Strings = typeof STUDENT_BUDGET_CALCULATOR_STRINGS;

const DEFAULTS: Record<string, string> = {
  job: "900",
  support: "300",
  other: "0",
  rent: "650",
  utilities: "80",
  groceries: "250",
  transport: "60",
  phone: "40",
  health: "50",
  supplies: "60",
  eatingOut: "120",
  entertainment: "60",
  subscriptions: "25",
  personal: "70",
  savings: "100",
  debt: "0",
  months: "4",
  terms: "2",
  tuition: "3500",
  aid: "3000",
};

function itemLabels(strings: Strings): Record<string, string> {
  return {
    job: strings.itemJob,
    support: strings.itemSupport,
    other: strings.itemOther,
    rent: strings.itemRent,
    utilities: strings.itemUtilities,
    groceries: strings.itemGroceries,
    transport: strings.itemTransport,
    phone: strings.itemPhone,
    health: strings.itemHealth,
    supplies: strings.itemSupplies,
    eatingOut: strings.itemEatingOut,
    entertainment: strings.itemEntertainment,
    subscriptions: strings.itemSubscriptions,
    personal: strings.itemPersonal,
    savings: strings.itemSavings,
    debt: strings.itemDebt,
  };
}

function ItemGroup({
  heading,
  items,
  values,
  labels,
  onChange,
  subtotal,
  subtotalLabel,
}: {
  heading: string;
  items: readonly string[];
  values: Record<string, string>;
  labels: Record<string, string>;
  onChange: (key: string, value: string) => void;
  subtotal: string;
  subtotalLabel: string;
}) {
  return (
    <section className={`${CARD} space-y-4`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className={WIDGET_H3}>{heading}</h2>
        <p className={HINT}>
          {subtotalLabel}: {subtotal}
        </p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {items.map((key) => (
          <TextField
            id={`sb-${key}`}
            key={key}
            label={labels[key]}
            numeric
            onChange={(value) => onChange(key, value)}
            value={values[key]}
          />
        ))}
      </div>
    </section>
  );
}

function Results({
  values,
  currency,
  strings,
}: {
  values: Record<string, string>;
  currency: string;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const money = useMoney(currency);
  const amount = (key: string) => Math.max(0, parseNumberOr(values[key], 0));
  const group = (keys: readonly string[]) => keys.map(amount);
  const totals = budgetTotals({
    income: group(INCOME_ITEMS),
    needs: group(NEEDS_ITEMS),
    wants: group(WANTS_ITEMS),
    savings: group(SAVINGS_ITEMS),
  });
  const term = termGap({
    totals,
    months: amount("months"),
    tuition: amount("tuition"),
    aid: amount("aid"),
    termsPerYear: amount("terms"),
  });
  const short = totals.balance < 0;
  const gapOpen = term.gap > 0;
  const groupNames = {
    needs: strings.needsHeading,
    wants: strings.wantsHeading,
    savings: strings.savingsHeading,
  };
  const percent = (v: number) =>
    `${fmt(v * 100, { maximumFractionDigits: 1 })}%`;
  return (
    <section aria-live="polite" className="space-y-5">
      <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
      <StatGrid>
        <StatCard label={strings.monthlyIncome} value={money(totals.income)} />
        <StatCard
          label={strings.monthlySpending}
          value={money(totals.spending)}
        />
        <StatCard
          emphasis
          label={short ? strings.shortBy : strings.leftOver}
          value={money(Math.abs(totals.balance))}
        />
      </StatGrid>
      <h3 className={WIDGET_H3}>{strings.splitHeading}</h3>
      {totals.income > 0 ? (
        <DataTable
          head={[
            strings.groupColumn,
            strings.amountColumn,
            strings.shareColumn,
            strings.targetColumn,
            strings.differenceColumn,
          ]}
          rows={splitRows(totals).map((row) => [
            groupNames[row.group],
            money(row.amount),
            row.share === null ? strings.noIncomeShare : percent(row.share),
            money(row.target),
            money(row.difference),
          ])}
        />
      ) : (
        <Notice>{strings.needIncome}</Notice>
      )}
      <p className={HINT}>{strings.splitNote}</p>
      <h3 className={WIDGET_H3}>{strings.termResultsHeading}</h3>
      <StatGrid>
        <StatCard label={strings.termCost} value={money(term.cost)} />
        <StatCard label={strings.termResources} value={money(term.resources)} />
        <StatCard
          emphasis
          label={gapOpen ? strings.termGapLabel : strings.termSurplusLabel}
          value={money(Math.abs(term.gap))}
        />
        <StatCard
          label={gapOpen ? strings.yearGapLabel : strings.yearSurplusLabel}
          value={money(Math.abs(term.yearGap))}
        />
        <StatCard
          label={gapOpen ? strings.perMonthLabel : strings.perMonthSurplusLabel}
          value={money(Math.abs(term.perMonth))}
        />
      </StatGrid>
      <p className={HINT}>{strings.termNote}</p>
    </section>
  );
}

export default function StudentBudgetCalculatorWidget() {
  const strings = useToolUi(STUDENT_BUDGET_CALCULATOR_STRINGS);
  const [currency, setCurrency] = useState("USD");
  const [values, setValues] = useState<Record<string, string>>(DEFAULTS);
  const money = useMoney(currency);
  const labels = itemLabels(strings);
  const set = (key: string, value: string) =>
    setValues((old) => ({ ...old, [key]: value }));
  const subtotal = (keys: readonly string[]) =>
    money(
      sumAmounts(keys.map((k) => Math.max(0, parseNumberOr(values[k], 0))))
    );

  return (
    <div className="space-y-6">
      <div className="max-w-xs">
        <CurrencyField
          id="sb-currency"
          label={strings.currency}
          onChange={setCurrency}
          plainLabel={strings.plainNumbers}
          value={currency}
        />
      </div>
      <p className={HINT}>{strings.monthlyHint}</p>
      <ItemGroup
        heading={strings.incomeHeading}
        items={INCOME_ITEMS}
        labels={labels}
        onChange={set}
        subtotal={subtotal(INCOME_ITEMS)}
        subtotalLabel={strings.subtotal}
        values={values}
      />
      <ItemGroup
        heading={strings.needsHeading}
        items={NEEDS_ITEMS}
        labels={labels}
        onChange={set}
        subtotal={subtotal(NEEDS_ITEMS)}
        subtotalLabel={strings.subtotal}
        values={values}
      />
      <ItemGroup
        heading={strings.wantsHeading}
        items={WANTS_ITEMS}
        labels={labels}
        onChange={set}
        subtotal={subtotal(WANTS_ITEMS)}
        subtotalLabel={strings.subtotal}
        values={values}
      />
      <ItemGroup
        heading={strings.savingsHeading}
        items={SAVINGS_ITEMS}
        labels={labels}
        onChange={set}
        subtotal={subtotal(SAVINGS_ITEMS)}
        subtotalLabel={strings.subtotal}
        values={values}
      />
      <section className={`${CARD} space-y-4`}>
        <h2 className={WIDGET_H3}>{strings.termHeading}</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="sb-months"
            label={strings.termMonths}
            numeric
            onChange={(value) => set("months", value)}
            value={values.months}
          />
          <TextField
            id="sb-terms"
            label={strings.termsPerYear}
            numeric
            onChange={(value) => set("terms", value)}
            value={values.terms}
          />
          <TextField
            id="sb-tuition"
            label={strings.tuition}
            numeric
            onChange={(value) => set("tuition", value)}
            value={values.tuition}
          />
          <TextField
            id="sb-aid"
            label={strings.aid}
            numeric
            onChange={(value) => set("aid", value)}
            value={values.aid}
          />
        </div>
      </section>
      <Results currency={currency} strings={strings} values={values} />
    </div>
  );
}
